import React from 'react';
import styled from 'styled-components';
import { ReactComponent as PdfIcon } from '../../../res/assets/document_pdf.svg';
import { ReactComponent as UrlIcon } from '../../../res/assets/document_url.svg';
import { ReactComponent as EditCircle } from '../../../res/assets/editCircle.svg';
import { ReactComponent as DeleteCircle } from '../../../res/assets/deleteCircle.svg';
import { ReactComponent as DownloadCircle } from '../../../res/assets/downloadCircle.svg';
import { ReactComponent as OpenLinkCircle } from '../../../res/assets/openLinkCircle.svg';
import colors from '../../../res/styles/Colors';
import dimension from '../../../res/styles/Responsive';
import { downloadResource } from '../../../service/ResourceService';

const SORT_FIELDS = {
    title: 'title',
    tags: 'tags',
    createdAt: 'createdAt'
};

function SortArrow({ field, sortField, sortDirection }) {
    if (sortField !== field) return <SortArrowPlaceholder />;
    return <SortArrowIcon>{sortDirection === 'asc' ? '▲' : '▼'}</SortArrowIcon>;
}

export default function ResourceTable({ resources, sortField, sortDirection, onSortChange, isAdmin, onEditClick, onDeleteClick }) {
    const handleHeaderClick = (field) => {
        if (sortField === field) {
            onSortChange(field, sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            onSortChange(field, 'asc');
        }
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return '-';
        return new Date(timestamp).toLocaleDateString('it-IT');
    };

    const formatTags = (tags) => {
        if (!tags || tags.length === 0) return '-';
        return tags.map((t) => t.name).join(', ');
    };

    return (
        <TableWrapper>
            {/* Desktop header — hidden on mobile */}
            <HeaderRow>
                <ColName>
                    <SortableCol onClick={() => handleHeaderClick(SORT_FIELDS.title)}>
                        Name
                        <SortArrow field={SORT_FIELDS.title} sortField={sortField} sortDirection={sortDirection} />
                    </SortableCol>
                </ColName>
                <ColTag>
                    <span>Tags</span>
                </ColTag>
                <ColDate>
                    <SortableCol onClick={() => handleHeaderClick(SORT_FIELDS.createdAt)}>
                        Date
                        <SortArrow field={SORT_FIELDS.createdAt} sortField={sortField} sortDirection={sortDirection} />
                    </SortableCol>
                </ColDate>
                <ColActions>
                    <span>Actions</span>
                </ColActions>
            </HeaderRow>

            <RowsContainer>
                {resources.map((resource) => (
                    <DataRow key={resource.id}>
                        {/* Desktop layout */}
                        <DesktopContent>
                            <ColName>
                                <NameCell>
                                    {resource.type === 'pdf' ? (
                                        <PdfIcon width={19} height={24} />
                                    ) : (
                                        <UrlIcon width={19} height={24} />
                                    )}
                                    <span>{resource.title}</span>
                                </NameCell>
                            </ColName>
                            <ColTag>{formatTags(resource.tags)}</ColTag>
                            <ColDate>{formatDate(resource.createdAt)}</ColDate>
                            <ColActions>
                                <ActionsCell>
                                    {resource.type === 'pdf' ? (
                                        <ActionButton onClick={() => downloadResource(resource.id, resource.title)}>
                                            Download
                                        </ActionButton>
                                    ) : (
                                        <ActionButton onClick={() => window.open(resource.url, '_blank')}>
                                            Open link
                                        </ActionButton>
                                    )}
                                    {isAdmin && (
                                        <>
                                            <CircleButton role='button' tabIndex={0} onClick={() => onEditClick(resource)}>
                                                <EditCircle width={32} height={32} />
                                            </CircleButton>
                                            <CircleButton role='button' tabIndex={0} onClick={() => onDeleteClick(resource)}>
                                                <DeleteCircle width={32} height={32} />
                                            </CircleButton>
                                        </>
                                    )}
                                </ActionsCell>
                            </ColActions>
                        </DesktopContent>

                        {/* Mobile card layout */}
                        <MobileCard>
                            <MobileCardInfo>
                                <MobileRow1>
                                    {resource.type === 'pdf' ? (
                                        <PdfIcon width={19} height={24} />
                                    ) : (
                                        <UrlIcon width={19} height={24} />
                                    )}
                                    <MobileTitle>{resource.title}</MobileTitle>
                                </MobileRow1>
                                <MobileMetaText>{formatTags(resource.tags)}</MobileMetaText>
                                <MobileMetaText>{formatDate(resource.createdAt)}</MobileMetaText>
                            </MobileCardInfo>
                            <MobileCardActions>
                                <CircleButton
                                    role='button'
                                    tabIndex={0}
                                    onClick={() =>
                                        resource.type === 'pdf'
                                            ? downloadResource(resource.id, resource.title)
                                            : window.open(resource.url, '_blank')
                                    }>
                                    {resource.type === 'pdf' ? (
                                        <DownloadCircle width={32} height={32} />
                                    ) : (
                                        <OpenLinkCircle width={32} height={32} />
                                    )}
                                </CircleButton>
                                {isAdmin && (
                                    <>
                                        <CircleButton role='button' tabIndex={0} onClick={() => onEditClick(resource)}>
                                            <EditCircle width={32} height={32} />
                                        </CircleButton>
                                        <CircleButton role='button' tabIndex={0} onClick={() => onDeleteClick(resource)}>
                                            <DeleteCircle width={32} height={32} />
                                        </CircleButton>
                                    </>
                                )}
                            </MobileCardActions>
                        </MobileCard>
                    </DataRow>
                ))}
                {resources.length === 0 && (
                    <EmptyRow>
                        <span>No resources found</span>
                    </EmptyRow>
                )}
            </RowsContainer>
        </TableWrapper>
    );
}

const colNameWidth = '40%';
const colTagWidth = '25%';
const colDateWidth = '17%';
const colActionsWidth = '18%';

const TableWrapper = styled.div`
    padding: 0 20px 20px;
`;

const RowBase = styled.div`
    display: flex;
    align-items: center;
`;

const HeaderRow = styled(RowBase)`
    display: none;
    padding: 8px 12px;
    font-size: 14px;
    font-weight: 400;
    color: ${colors.darkest_gray};

    @media ${dimension.md} {
        display: flex;
    }
`;

const RowsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const DataRow = styled.div`
    background-color: white;
    padding: 8px 12px;
    min-height: 48px;

    @media (max-width: 767px) {
        padding: 14px 16px;
    }
`;

const DesktopContent = styled.div`
    display: none;
    align-items: center;
    width: 100%;

    @media ${dimension.md} {
        display: flex;
    }
`;

const MobileCard = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 12px;

    @media ${dimension.md} {
        display: none;
    }
`;

const MobileCardInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
    min-width: 0;
`;

const MobileCardActions = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
`;

const MobileRow1 = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 14px;

    svg {
        flex-shrink: 0;
        width: 24px;
        height: 30px;
    }
`;

const MobileTitle = styled.span`
    font-size: 16px;
    font-weight: 600;
    color: ${colors.darkest_gray};
    line-height: 1.3;
    margin-top: 5px;
`;

const MobileMetaText = styled.span`
    font-size: 14px;
    color: ${colors.dark_gray};
    padding-left: 38px;
`;

const ColName = styled.div`
    width: ${colNameWidth};
    min-width: 0;
    padding-right: 16px;
`;

const ColTag = styled.div`
    width: ${colTagWidth};
    flex-shrink: 0;
    font-size: 14px;
    color: ${colors.darkest_gray};
    padding-right: 16px;
`;

const ColDate = styled.div`
    width: ${colDateWidth};
    flex-shrink: 0;
    font-size: 14px;
    color: ${colors.darkest_gray};
    padding-right: 16px;
`;

const ColActions = styled.div`
    width: ${colActionsWidth};
    flex-shrink: 0;
    display: flex;
    align-items: center;
`;

const SortableCol = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    user-select: none;
    white-space: nowrap;

    &:hover {
        color: ${colors.green};
    }
`;

const SortArrowIcon = styled.span`
    font-size: 9px;
    color: ${colors.green};
`;

const SortArrowPlaceholder = styled.span`
    display: inline-block;
    width: 9px;
`;

const NameCell = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;

    svg {
        flex-shrink: 0;
    }

    span {
        font-size: 14px;
        color: ${colors.darkest_gray};
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`;

const ActionsCell = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;

const ActionButton = styled.button`
    width: 120px;
    height: 32px;
    background-color: ${colors.lighter_green};
    color: ${colors.green};
    border: none;
    border-radius: 26px;
    font-size: 12px;
    cursor: pointer;
    text-align: center;
    white-space: nowrap;

    &:hover {
        background-color: ${colors.light_green};
    }
`;

const CircleButton = styled.div`
    display: inline-flex;
    flex-shrink: 0;
    cursor: pointer;

    svg {
        width: 32px;
        height: 32px;
    }

    &:hover {
        opacity: 0.8;
    }
`;

const EmptyRow = styled.div`
    background-color: white;
    padding: 40px;
    text-align: center;
    color: ${colors.dark_gray};
    font-size: 14px;
`;
